// ============================================================
// ClientPickerModal.tsx
// Pick an existing Client from the firm's directory (search + list) or
// create one inline; the modal calls onClientSelected(clientId). Used by the
// CSV import and Tasks. (Cases are created from NewCaseModal /
// NewCaseForClientModal — they need a claim type, which this modal doesn't ask.)
//
// Phase 3 of the Client work. Built from scratch — the previous
// "Phase 3 Client picker" commit (db70ecc) only edited README,
// no UI was actually shipped.
//
// Talks to the existing Phase 2 store actions
// (loadClients, createClient) — does NOT call the API directly,
// so errors / loading / list state stay consistent with the rest
// of the app's Client UI.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { useCasevoStore } from '../../store/useCasevoStore'
import { canDeleteClients } from '../../utils/casePermissions'
import type { Client, CreateClientPayload } from '../../types/client.types'
import AddressLookup from './AddressLookup'
import DateInput from './DateInput'
import { dobError, dobWarning } from '../../utils/caseDateRules'
import { londonToday } from '../../utils/civilDate'
import FieldError from './FieldError'
import ActionBox from './ActionBox'
import { emailFormatError, phoneFormatError } from '../../utils/contactFormat'
interface ClientPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onClientSelected: (clientId: string) => void
  // Optional UI mode. Defaults to 'both' so legacy callers that don't
  // pass this prop keep the original "list + collapsible create form"
  // layout. 'pick' renders only the existing-clients section, 'create'
  // renders only the form (auto-expanded, no chevron). The case-list
  // header now drives this with two separate buttons.
  mode?: 'pick' | 'create' | 'both'
}
// Title options for the "Create New Client" form. Empty string =
// no title selected, which maps to `null` on the server.
const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof'] as const
const SEARCH_DEBOUNCE_MS = 300
const ClientPickerModal: React.FC<ClientPickerModalProps> = ({
  isOpen,
  onClose,
  onClientSelected,
  mode = 'both',
}) => {
  // (Audit batch 3, H4) A search can return walled clients as bare
  // "Restricted client" stubs — a picker can't use them, so they're left out.
  const allClients = useCasevoStore((s) => s.clients)
  const clients = useMemo(() => allClients.filter((c) => !c.restricted), [allClients])
  const authUser = useCasevoStore((s) => s.authUser)
  const canDelete = canDeleteClients(authUser)
  const clientsLoading = useCasevoStore((s) => s.clientsLoading)
  const clientsError = useCasevoStore((s) => s.clientsError)
  const loadClients = useCasevoStore((s) => s.loadClients)
  const createClient = useCasevoStore((s) => s.createClient)
  const deleteClient = useCasevoStore((s) => s.deleteClient)
  // ── search ───────────────────────────────────────────────
  const [search, setSearch] = useState('')
  // ── sort ─────────────────────────────────────────────────
  // 'az' / 'za' reorder client-side over whatever the backend
  // returned. 'recent' is backend-driven (P6 — ?sort=recent on
  // GET /api/clients orders by Client.lastAccessedAt desc NULLS
  // LAST) and the sortedClients useMemo below preserves that
  // order rather than re-sorting locally.
  const [sortMode, setSortMode] = useState<'az' | 'za' | 'recent'>('az')
  // ── keyboard nav ─────────────────────────────────────────
  // Index of the keyboard-highlighted row in sortedClients.
  // null = nothing highlighted. Arrow keys move it; Enter picks
  // the highlighted row. Reset whenever the list context shifts
  // (modal open, search change, sort change) and clamped when
  // the list shrinks past the index — see the related effects
  // around the keydown handler below.
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
  // ── create-form state ────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false)
  const [formTitle, setFormTitle] = useState('')
  const [formForename, setFormForename] = useState('')
  const [formSurname, setFormSurname] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formPhone, setFormPhone] = useState('')
  // Phone / email left (blurred) → their format error shows (utils/contactFormat).
  const [leftContact, setLeftContact] = useState<{ phone?: boolean; email?: boolean }>({})
  const [formAddressLine1, setFormAddressLine1] = useState('')
  const [formAddressLine2, setFormAddressLine2] = useState('')
  const [formAddressTown, setFormAddressTown] = useState('')
  const [formAddressCounty, setFormAddressCounty] = useState('')
  const [formAddressPostcode, setFormAddressPostcode] = useState('')
  const [formDateOfBirth, setFormDateOfBirth] = useState('')
  // DATE HARDENING: Create waits while the DOB is half-typed or in the future.
  const [dobTyping, setDobTyping] = useState(false)
  const [creating, setCreating] = useState(false)
  // Unsaved-changes guard: the "new client" form has something typed —
  // ×, Esc, backdrop, Cancel (and leaving the screen / Back / refresh) ask.
  const guardClose = useUnsavedChangesGuard(isOpen && formOpen && !creating && [
    formTitle, formForename, formSurname, formEmail, formPhone, formAddressLine1, formAddressLine2,
    formAddressTown, formAddressCounty, formAddressPostcode, formDateOfBirth,
  ].some((v) => v.trim() !== ''))
  const requestClose = (): void => guardClose(onClose)
  const [createError, setCreateError] = useState<string | null>(null)
  // ── delete-confirmation state ────────────────────────────
  // confirmDeleteId  — the row currently asking "are you sure?".
  // deletingId       — the row whose DELETE request is in flight.
  // deleteError      — backend message (esp. the 409 "client has
  //                    active cases" text) shown inline on the row.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // ── refs ─────────────────────────────────────────────────
  const overlayRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const forenameRef = useRef<HTMLInputElement>(null)
  // Per-row DOM refs so the scroll-into-view effect can target the
  // highlighted row. Indexed to match sortedClients order; populated
  // by the ref callback in the list map below.
  const rowRefs = useRef<(HTMLLIElement | null)[]>([])
  // Latest-state ref read by the window keydown handler so the
  // listener stays stable across renders (no re-register on every
  // arrow press). Assigned below, after sortedClients is computed,
  // so the handler always sees the freshest values at keydown time.
  const latestKeyboardStateRef = useRef<{
    sortedClients: Client[]
    highlightedIndex: number | null
    confirmDeleteId: string | null
  }>({
    sortedClients: [],
    highlightedIndex: null,
    confirmDeleteId: null,
  })
  // Restore focus to whatever was focused before the modal opened
  // (typically the "+ New" button) when the modal closes.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  // Reset transient UI state every time the modal opens. We
  // deliberately DON'T reset `search` to '' on every open — keeping
  // it would be reasonable too, but Reset-on-open feels cleaner
  // when the trigger button is in the page header.
  useEffect(() => {
    if (!isOpen) return
    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    setSearch('')
    setSortMode('az')
    setFormTitle('')
    setFormForename('')
    setFormSurname('')
    setFormEmail('')
    setFormPhone('')
    setLeftContact({})
    setFormAddressLine1('')
    setFormAddressLine2('')
    setFormAddressTown('')
    setFormAddressCounty('')
    setFormAddressPostcode('')
    setFormDateOfBirth('')
    setCreating(false)
    setCreateError(null)
    setConfirmDeleteId(null)
    setDeletingId(null)
    setDeleteError(null)
    // Fire an initial load so the list is populated. If the store
    // already has clients loaded that's fine — loadClients refreshes
    // them. If the firm has zero clients we'll auto-expand the form
    // below once the load resolves.
    void loadClients()
    return () => {
      previouslyFocusedRef.current?.focus?.()
    }
  }, [isOpen, loadClients])
  // Auto-expand the create form when the firm has no clients yet AND
  // there's no search in progress. Once the user starts searching,
  // we let them control the expand state manually so they don't have
  // the form unexpectedly disappearing under them.
  //
  // Pick-mode opt-out: in 'pick' mode the form is hidden anyway, so
  // flipping formOpen would just dirty state. Bail early. ('both' and
  // 'create' both render the form when appropriate.)
  useEffect(() => {
    if (!isOpen) return
    if (mode === 'pick') return
    if (clientsLoading) return
    if (search.trim() !== '') return
    if (clients.length === 0) setFormOpen(true)
  }, [isOpen, mode, clientsLoading, clients.length, search])
  // Auto-focus on open. Defer with rAF so the element exists in the
  // DOM before we ask for focus.
  //   - 'pick' mode → always focus the search input (no form rendered).
  //   - 'create' mode → always focus Forename (no list rendered).
  //   - 'both' mode → Forename when the form is auto-expanded for a
  //     zero-clients firm, otherwise the search input.
  useEffect(() => {
    if (!isOpen) return
    const id = requestAnimationFrame(() => {
      if (mode === 'pick') {
        searchRef.current?.focus()
      } else if (mode === 'create') {
        forenameRef.current?.focus()
      } else if (clients.length === 0 && !clientsLoading && formOpen) {
        forenameRef.current?.focus()
      } else {
        searchRef.current?.focus()
      }
    })
    return () => cancelAnimationFrame(id)
  }, [isOpen, mode, clients.length, clientsLoading, formOpen])
  // Debounced search — 300ms after the last keystroke, hit the API.
  // Empty search loads the full list. We don't fire on the initial
  // open render because the open-effect above already kicked off a
  // load with no search term.
  const firstSearchEffectRef = useRef(true)
  useEffect(() => {
    if (!isOpen) return
    if (firstSearchEffectRef.current) {
      firstSearchEffectRef.current = false
      return
    }
    const timer = setTimeout(() => {
      void loadClients(
        search.trim() || undefined,
        sortMode === 'recent' ? 'recent' : undefined,
      )
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search, sortMode, isOpen, loadClients])
  // Reset the "first effect" guard whenever the modal closes so the
  // next open starts clean.
  useEffect(() => {
    if (!isOpen) firstSearchEffectRef.current = true
  }, [isOpen])
  // Drop any pending in-row delete confirmation when the search term
  // changes. Without this, typing in search hides the confirmation
  // (because the row is filtered out) and clearing search makes it
  // reappear unexpectedly. Doesn't cancel an in-flight delete — the
  // API call carries on and the row leaves clients[] on success
  // regardless of this state.
  useEffect(() => {
    setConfirmDeleteId(null)
    setDeleteError(null)
  }, [search])
  // Escape to close + focus trap (Tab cycles within the modal) +
  // arrow-key nav through the client list. One stable window-level
  // keydown listener; freshest list/highlight/delete-confirm state
  // is read via latestKeyboardStateRef at keydown time so the
  // listener doesn't have to re-register on every state change.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        guardClose(onClose)
        return
      }
      // Arrow / Enter list nav. The search input is EXEMPT from
      // suppression so the user can type-and-arrow without clicking
      // off the search box (mirrors Google search / command-palette
      // UX). All OTHER inputs / textareas / selects (title <select>,
      // create-form fields) remain suppressed — Enter on "Forename"
      // shouldn't pick a client by accident. Arrows always move the
      // highlight past a delete-confirm row without skipping it;
      // Enter on a delete-confirm row no-ops (the Cancel / Delete
      // buttons own that branch).
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        const target = e.target as HTMLElement | null
        const tag = target?.tagName
        const isSearchInput = target === searchRef.current
        if (!isSearchInput && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')) return
        const {
          sortedClients: list,
          highlightedIndex: hi,
          confirmDeleteId: cdId,
        } = latestKeyboardStateRef.current
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setHighlightedIndex(
            list.length === 0
              ? null
              : hi === null
                ? 0
                : Math.min(hi + 1, list.length - 1),
          )
          return
        }
        if (e.key === 'ArrowUp') {
          if (hi === null) return
          e.preventDefault()
          setHighlightedIndex(Math.max(hi - 1, 0))
          return
        }
        // Enter
        if (hi === null) return
        const c = list[hi]
        if (!c) return
        if (cdId === c.id) return
        e.preventDefault()
        onClientSelected(c.id)
        return
      }
      if (e.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement)
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose, onClientSelected, guardClose])
  // Reset the keyboard highlight whenever the list context shifts —
  // the index becomes meaningless when the rows reorder (sort), the
  // list changes (search), or the modal opens fresh.
  useEffect(() => {
    setHighlightedIndex(null)
  }, [isOpen, search, sortMode])
  // Scroll the highlighted row into the visible portion of the
  // max-h-72 scroll container. block: 'nearest' is intentional —
  // only scrolls when the row isn't already on-screen, so casual
  // arrow presses in a visible list don't trigger unnecessary
  // animation.
  useEffect(() => {
    if (highlightedIndex === null) return
    rowRefs.current[highlightedIndex]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedIndex])
  // Client-side sort over whatever the backend returned. Search and
  // sort compose: the API filters via the existing 300ms debounced
  // query; sort just reorders the result. Sort key is
  // `${surname} ${forename}` (locale-aware) so the user's mental
  // model of "alphabetical" matches phone-book ordering. Lives up
  // here (above the early return) because hooks must run in the
  // same order on every render — placing this below the
  // `if (!isOpen) return null` guard skipped the hook on closed
  // renders and tripped react-hooks/rules-of-hooks.
  const sortedClients = useMemo(() => {
    // 'recent' mode: backend already ordered by lastAccessedAt desc
    // NULLS LAST. Re-sorting client-side would lose that ordering.
    if (sortMode === 'recent') return clients
    const key = (c: Client) => `${c.surname} ${c.forename}`.toLowerCase()
    const sorted = [...clients].sort((a, b) => key(a).localeCompare(key(b), undefined, { numeric: true, sensitivity: 'base' }))
    if (sortMode === 'za') sorted.reverse()
    return sorted
  }, [clients, sortMode])
  // Surface the freshest list / highlight / delete-confirm state
  // to the keydown handler via the latestRef pattern. Runs every
  // render so the handler's reads at keydown time always see the
  // post-render values.
  latestKeyboardStateRef.current = {
    sortedClients,
    highlightedIndex,
    confirmDeleteId,
  }
  // Clamp the highlight if the list shrank past the index (e.g.
  // a delete removed a row). Snaps to the new last row, or clears
  // if the list emptied entirely. The reset effect above handles
  // the search / sort / open paths separately; this only catches
  // the case where the list shrinks without a context switch.
  useEffect(() => {
    if (highlightedIndex === null) return
    if (highlightedIndex >= sortedClients.length) {
      setHighlightedIndex(
        sortedClients.length > 0 ? sortedClients.length - 1 : null,
      )
    }
  }, [sortedClients.length, highlightedIndex])
  if (!isOpen) return null
  // Pick an existing client. Closes the modal — the parent decides
  // what to do with the id (typically createCaseApi).
  const handlePick = (client: Client) => {
    onClientSelected(client.id)
  }
  // Submit the create form. On success, behaves the same as picking
  // an existing client: notify the parent with the new id.
  const handleCreate = async () => {
    if (creating) return
    const forename = formForename.trim()
    const surname = formSurname.trim()
    const phone = formPhone.trim()
    // Local validation: forename, surname, phone are required. Set
    // createError to drive both the inline message and the red-border
    // highlight on empty required inputs (via requiredInputClass).
    if (!forename || !surname || !phone) {
      setCreateError('Forename, surname, and main contact number are required.')
      return
    }
    // A real phone / email (utils/contactFormat) — FieldError under each.
    if (phoneFormatError(phone) || emailFormatError(formEmail)) {
      setLeftContact({ phone: true, email: true })
      return
    }
    setCreating(true)
    setCreateError(null)
    // CreateClientPayload covers every editable field on the client
    // record; the picker now collects the commonly-known-at-first-
    // contact fields (name, address, DOB, phone, email) and defers
    // NI / employment / occupation to the Client Details section.
    const payload: CreateClientPayload = {
      title: formTitle || null,
      forename,
      surname,
      addressLine1: formAddressLine1.trim(),
      addressLine2: formAddressLine2.trim(),
      addressTown: formAddressTown.trim(),
      addressCounty: formAddressCounty.trim(),
      addressPostcode: formAddressPostcode.trim(),
      dateOfBirth: formDateOfBirth || null,
      hasNiNumber: null,
      niNumber: '',
      mainContactNumber: phone,
      emailAddress: formEmail.trim(),
      employmentStatus: null,
      occupation: '',
    }
    try {
      const newClient = await createClient(payload)
      onClientSelected(newClient.id)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create client'
      setCreateError(message)
      setCreating(false)
    }
  }
  const handleCancelCreate = () => {
    setFormOpen(false)
    setFormTitle('')
    setFormForename('')
    setFormSurname('')
    setFormEmail('')
    setFormPhone('')
    setLeftContact({})
    setFormAddressLine1('')
    setFormAddressLine2('')
    setFormAddressTown('')
    setFormAddressCounty('')
    setFormAddressPostcode('')
    setFormDateOfBirth('')
    setCreateError(null)
  }
  // Click the trash icon — opens the inline "are you sure?" confirm
  // for that row. Clears any prior error.
  const handleDeleteClick = (c: Client) => {
    setConfirmDeleteId(c.id)
    setDeleteError(null)
  }
  // Cancel the in-row confirm — return the row to its normal state.
  const handleConfirmCancel = () => {
    setConfirmDeleteId(null)
    setDeleteError(null)
  }
  // Confirm — fire the API. On success the store removes the row from
  // clients[] and the confirmation unmounts naturally. On failure
  // (notably 409 when the client has active cases) surface the
  // backend's message inline so the user sees a clear reason.
  const handleConfirmDelete = async (c: Client) => {
    if (deletingId === c.id) return
    setDeletingId(c.id)
    setDeleteError(null)
    try {
      await deleteClient(c.id)
      // Row is gone from clients[]; the confirmation row unmounts.
      // Clear state defensively in case anything keeps a ref.
      setConfirmDeleteId(null)
      setDeletingId(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete client'
      setDeleteError(message)
      setDeletingId(null)
    }
  }
  // Clicking the backdrop closes — but ignore clicks inside the
  // dialog itself (they bubble up).
  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) guardClose(onClose)
  }
  const dobBlock = dobError(formDateOfBirth || null, londonToday())
  const createDisabled = creating || !formForename.trim() || !formSurname.trim() || !formPhone.trim() || dobTyping || !!dobBlock
  const phoneErr = leftContact.phone ? phoneFormatError(formPhone) : null
  const emailErr = leftContact.email ? emailFormatError(formEmail) : null
  // Re-used input class — matches the rest of the app's form inputs.
  const inputBase =
    'w-full border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid'
  const inputClass = `${inputBase} border-casevo-border`
  // Red-bordered variant for required fields after a failed validation
  // attempt: stays red while `createError` is set AND the field is
  // still empty after trim. Once the user fills it the border clears
  // on that field, though the inline error message persists until the
  // next submit succeeds.
  const requiredInputClass = (value: string): string =>
    `${inputBase} ${createError && !value.trim() ? 'border-red-300' : 'border-casevo-border'}`
  // Compose the row label for an existing-client list entry. Title
  // is optional; client number always present (Phase 1 auto-assigns).
  const rowLabel = (c: Client): string => {
    const namePart = [c.title, c.forename, c.surname].filter(Boolean).join(' ')
    return `${namePart} — Client #${c.clientNumber}`
  }
  // The empty-list message depends on whether the user is searching
  // AND on the mode — pick-only mode has no create form below, so the
  // "below" hint would be misleading.
  const emptyMessage = (): string => {
    if (search.trim()) {
      return mode === 'pick'
        ? `No clients match "${search.trim()}".`
        : `No clients match "${search.trim()}". Create a new client below.`
    }
    return mode === 'pick'
      ? 'No clients yet. Use the + New button to add one.'
      : 'No clients yet. Create your first client below.'
  }
  // Sort-toolbar button class. Three visual states: active (mint),
  // inactive (white with mid-hover), disabled (muted + cursor-not-
  // allowed — only the "Recently used" button uses disabled today).
  const sortBtnClass = (active: boolean, disabled: boolean = false): string => {
    const base = 'text-xs uppercase tracking-widest px-3 py-1 rounded border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid'
    if (disabled) return `${base} bg-casevo-surface border-casevo-border text-casevo-mid opacity-50 cursor-not-allowed`
    if (active) return `${base} bg-casevo-mint border-casevo-mid text-casevo-forest`
    return `${base} bg-casevo-surface border-casevo-border text-casevo-mid hover:border-casevo-mid`
  }
  return (
    <div
      ref={overlayRef}
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4 py-6"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-picker-title"
        className="bg-casevo-surface rounded-lg border border-casevo-border w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-casevo-border flex-shrink-0">
          <h3
            id="client-picker-title"
            className="text-sm font-medium text-casevo-forest uppercase tracking-widest"
          >
            {mode === 'pick'
              ? 'Open Existing Client'
              : mode === 'create'
                ? 'Create New Client'
                : 'Select or Create Client'}
          </h3>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="text-casevo-mid hover:text-casevo-forest text-lg leading-none px-2 -mr-2"
          >
            ✕
          </button>
        </div>
        {/* Scrollable body — wraps the search, list, and create-form
            sections so the modal scrolls when the create form
            expands past 90vh. The header above stays fixed
            (flex-shrink-0). The list inside still has its own
            max-h-72 + overflow-y-auto for many-client firms —
            nested scroll is fine. The wrapper has NO padding of its
            own; each inner sibling keeps its existing padding so
            visual spacing is unchanged from the previous layout. */}
        <div className="flex-1 overflow-y-auto">
        {/* Section 1: Existing Clients — heading + icon search +
            sort toolbar. Search hits the backend (existing 300ms
            debounce path, query goes via the API); sort runs
            client-side over the returned array.
            Hidden entirely in 'create' mode — the create flow has no
            need for the list, and removing it (rather than just
            stylistically hiding it) keeps the auto-focus and load
            effects from running against an off-screen list. */}
        {mode !== 'create' && (
        <>
        <div className="px-6 pt-4 flex flex-col gap-3">
          <h3 className="text-xs font-medium text-casevo-forest uppercase tracking-widest">
            Existing Clients
          </h3>
          <div className="relative">
            {/* Leading magnifying-glass — pointer-events-none so the
                input still captures clicks across the full width. */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-casevo-mid pointer-events-none"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              aria-label="Search clients by name"
              placeholder="Search clients by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${inputClass} pl-10`}
            />
          </div>
          {/* Sort toolbar — right-aligned to keep visual weight on
              the search-then-list flow; sort is a quieter refinement.
              'Recently used' hits the backend's ?sort=recent endpoint
              (P6 — orders by Client.lastAccessedAt desc NULLS LAST).
              Includes search filtering when both are active; the
              search effect above is the single source of truth for
              re-fetching when either input changes. */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setSortMode('az')}
              aria-pressed={sortMode === 'az'}
              className={sortBtnClass(sortMode === 'az')}
            >
              A–Z
            </button>
            <button
              type="button"
              onClick={() => setSortMode('za')}
              aria-pressed={sortMode === 'za'}
              className={sortBtnClass(sortMode === 'za')}
            >
              Z–A
            </button>
            <button
              type="button"
              onClick={() => setSortMode('recent')}
              aria-pressed={sortMode === 'recent'}
              className={sortBtnClass(sortMode === 'recent')}
            >
              Recently used
            </button>
          </div>
        </div>
        {/* List — capped at ~5 rows (max-h-52). The old flex-1
            sizing dominated the modal even with two clients; capping
            lets the modal shrink to fit content when the firm is
            small, and scrolls only when content overflows. */}
        <div className="max-h-72 overflow-y-auto px-6 py-4">
          {clientsError && (
            <ActionBox alert tone="danger" className="mb-3" message={clientsError}
              actions={[{
                label: 'Retry',
                kind: 'link',
                onClick: () => void loadClients(
                  search.trim() || undefined,
                  sortMode === 'recent' ? 'recent' : undefined,
                ),
              }]} />
          )}
          {clientsLoading && (
            // Skeleton rows during initial fetch — same pattern
            // CaseListScreen uses for its case-list skeleton, scaled
            // to the picker's tighter row geometry. Three pulsing
            // rows that mirror the real row shape (flex-1 pick area
            // + small trash square). animate-pulse on the row
            // container so both placeholders pulse in sync. The
            // <ul className="flex flex-col gap-1"> wrapper matches
            // the loaded-list wrapper below so the transition from
            // skeleton → loaded rows doesn't visually shift the
            // layout.
            <ul className="flex flex-col gap-1">
              {[0, 1, 2].map((i) => (
                <li key={i}>
                  <div className="flex items-stretch gap-1 animate-pulse">
                    {/* Wide pick placeholder — outer mirrors the
                        real pick button's flex-1 px-3 py-2 rounded;
                        inner bar is the w-48 / h-4 mint shape that
                        suggests a client-name label. */}
                    <div className="flex-1 px-3 py-2 rounded">
                      <div className="w-48 h-4 bg-casevo-mint/40 rounded" />
                    </div>
                    {/* Narrow trash placeholder — square roughly
                        matching the real trash button's hit area.
                        Lighter mint to read as secondary. */}
                    <div className="w-9 h-9 bg-casevo-mint/30 rounded" />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!clientsLoading && !clientsError && clients.length === 0 && (
            <p className="text-sm text-casevo-mid py-4 text-center italic">{emptyMessage()}</p>
          )}
          {!clientsLoading && clients.length > 0 && (
            <ul className="flex flex-col gap-1">
              {sortedClients.map((c, index) => (
                <li
                  key={c.id}
                  ref={(el) => {
                    rowRefs.current[index] = el
                  }}
                >
                  {confirmDeleteId === c.id ? (
                    // Inline confirmation — replaces the row's content
                    // until the user confirms or cancels. Kept inline
                    // (rather than stacking another modal over the
                    // picker) to avoid modal-on-modal awkwardness.
                    <div role="alertdialog" aria-label={`Confirm delete ${rowLabel(c)}`}>
                      <ActionBox tone="danger" ariaLabel={`Delete ${rowLabel(c)}`}
                        title={<>Delete <strong>{rowLabel(c)}</strong>?</>}
                        message="This can't be undone."
                        actions={[
                          { label: 'Cancel', kind: 'link', onClick: handleConfirmCancel, disabled: deletingId === c.id },
                          { label: 'Delete', kind: 'danger', icon: 'trash', onClick: () => void handleConfirmDelete(c), busy: deletingId === c.id, busyLabel: 'Deleting…' },
                        ]}
                      >
                        {deleteError && <p role="alert" className="m-0 mt-[6px] text-[12.5px] text-casevo-danger">{deleteError}</p>}
                      </ActionBox>
                    </div>
                  ) : (
                    // Normal row — pick button takes the full width
                    // less the trash; trash icon is a separate sibling
                    // button so clicks don't bubble between them and
                    // each has its own focus ring.
                    <div className="flex items-stretch gap-1">
                      <button
                        type="button"
                        onClick={() => handlePick(c)}
                        className={`flex-1 text-left px-3 py-2 rounded text-sm text-casevo-forest hover:bg-casevo-mint focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid transition-colors ${
                          index === highlightedIndex ? 'bg-casevo-mint' : ''
                        }`}
                      >
                        {rowLabel(c)}
                      </button>
                      {/* (Audit batch 3) Only for "Delete clients" holders / admins. */}
                      {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(c)}
                        aria-label={`Delete ${rowLabel(c)}`}
                        className="px-3 py-2 rounded text-casevo-mid hover:text-casevo-danger hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 transition-colors"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        </>
        )}
        {/* Section 2: Create New Client — collapsible in 'both' mode
            (chevron toggle, auto-expanded when zero clients) and
            unconditionally expanded in 'create' mode (no chevron;
            the whole modal IS the form). Hidden entirely in 'pick'
            mode. The border-t divider is only meaningful when
            Section 1 sits above us. */}
        {mode !== 'pick' && (
        <div className={`px-6 py-4 ${mode === 'create' ? '' : 'border-t border-casevo-border'}`}>
          {mode === 'both' && (
          <button
            type="button"
            onClick={() => setFormOpen((v) => !v)}
            aria-expanded={formOpen}
            className="w-full flex items-center justify-between text-xs uppercase tracking-widest hover:bg-casevo-mint/30 -mx-2 px-2 py-2 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid"
          >
            <span className="text-casevo-brand">Create New Client</span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={`text-casevo-mid transition-transform ${formOpen ? 'rotate-90' : ''}`}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          )}
          {(mode === 'create' || formOpen) && (
            // Vertical stack — every input gets its own row, full-
            // width within the form container. Title was previously
            // grouped with Forename in a flex row, which overflowed
            // off the right edge of the modal on narrow viewports.
            // The button row at the bottom keeps its own flex
            // (Cancel + Create Client side-by-side is intentional).
            <div className="mt-3 flex flex-col gap-3">
              <select
                aria-label="Title"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className={inputClass}
              >
                <option value="">Title (optional)</option>
                {TITLE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <input
                ref={forenameRef}
                type="text"
                aria-label="Forename"
                placeholder="Forename"
                value={formForename}
                onChange={(e) => setFormForename(e.target.value)}
                className={requiredInputClass(formForename)}
                required
              />
              <input
                type="text"
                aria-label="Surname"
                placeholder="Surname"
                value={formSurname}
                onChange={(e) => setFormSurname(e.target.value)}
                className={requiredInputClass(formSurname)}
                required
              />
              {/* Address autocomplete sits ABOVE the 5 manual inputs.
                  Picking a suggestion populates all 5 setters; the
                  inputs below remain editable for manual correction
                  (flats, new builds, addresses the autocomplete
                  misses). value is null when every field is empty so
                  the mint confirmation box only renders once there's
                  something to confirm. */}
              <AddressLookup
                value={
                  formAddressLine1 || formAddressLine2 || formAddressTown ||
                  formAddressCounty || formAddressPostcode
                    ? {
                        line1: formAddressLine1,
                        line2: formAddressLine2,
                        town: formAddressTown,
                        county: formAddressCounty,
                        postcode: formAddressPostcode,
                      }
                    : null
                }
                onSelect={(addr) => {
                  setFormAddressLine1(addr.line1)
                  setFormAddressLine2(addr.line2)
                  setFormAddressTown(addr.town)
                  setFormAddressCounty(addr.county)
                  setFormAddressPostcode(addr.postcode)
                }}
                onClear={() => {
                  setFormAddressLine1('')
                  setFormAddressLine2('')
                  setFormAddressTown('')
                  setFormAddressCounty('')
                  setFormAddressPostcode('')
                }}
              />
              <input
                type="text"
                aria-label="Address line 1"
                placeholder="Address line 1 (optional)"
                value={formAddressLine1}
                onChange={(e) => setFormAddressLine1(e.target.value)}
                className={inputClass}
              />
              <input
                type="text"
                aria-label="Address line 2"
                placeholder="Address line 2 (optional)"
                value={formAddressLine2}
                onChange={(e) => setFormAddressLine2(e.target.value)}
                className={inputClass}
              />
              <input
                type="text"
                aria-label="Town"
                placeholder="Town (optional)"
                value={formAddressTown}
                onChange={(e) => setFormAddressTown(e.target.value)}
                className={inputClass}
              />
              <input
                type="text"
                aria-label="County"
                placeholder="County (optional)"
                value={formAddressCounty}
                onChange={(e) => setFormAddressCounty(e.target.value)}
                className={inputClass}
              />
              <input
                type="text"
                aria-label="Postcode"
                placeholder="Postcode (optional)"
                value={formAddressPostcode}
                onChange={(e) => setFormAddressPostcode(e.target.value.toUpperCase())}
                className={inputClass}
              />
              {/* ★ Every other field here is self-describing via its
                  placeholder, but the date boxes only showed "DD / MM /
                  YYYY" — with no label they could be read as the ACCIDENT
                  date (which drives limitation). An explicit label + hint
                  makes it unmistakably the client's date of birth. */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-casevo-forest mb-1">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-casevo-mid" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" />
                  </svg>
                  Client&rsquo;s date of birth <span className="font-normal text-casevo-mid">(optional)</span>
                </label>
                <DateInput
                  value={formDateOfBirth || null}
                  onChange={(value) => setFormDateOfBirth(value || '')}
                  onValidityChange={(ok) => setDobTyping(!ok)}
                  fieldClassName={inputClass}
                  ariaLabel="Client's date of birth"
                />
                {dobBlock ? <p className="mt-1 text-xs text-casevo-danger" role="alert">{dobBlock}</p>
                  : dobWarning(formDateOfBirth || null, londonToday()) && <p className="mt-1 text-xs text-casevo-warn" role="status">{dobWarning(formDateOfBirth || null, londonToday())}</p>}
                <p className="mt-1 text-[11px] text-casevo-mid">
                  DD / MM / YYYY &middot; this is <span className="font-semibold">not</span> the accident date &mdash; that&rsquo;s added on the case.
                </p>
              </div>
              <input
                type="email"
                aria-label="Email"
                placeholder="Email (optional)"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                onBlur={() => setLeftContact((l) => ({ ...l, email: true }))}
                aria-invalid={emailErr ? true : undefined}
                aria-describedby={emailErr ? 'cp-email-err' : undefined}
                className={inputClass}
              />
              {emailErr && <span id="cp-email-err"><FieldError message={emailErr} /></span>}
              <input
                type="tel"
                aria-label="Main contact number"
                placeholder="Main contact number"
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
                onBlur={() => setLeftContact((l) => ({ ...l, phone: true }))}
                aria-invalid={phoneErr ? true : undefined}
                aria-describedby={phoneErr ? 'cp-phone-err' : undefined}
                className={requiredInputClass(formPhone)}
                required
              />
              {phoneErr && <span id="cp-phone-err"><FieldError message={phoneErr} /></span>}
              {createError && (
                <p className="text-xs text-casevo-danger">{createError}</p>
              )}
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  // In 'create' mode there's no picker to fall back to,
                  // so Cancel closes the whole modal. In 'both' mode it
                  // still just collapses the form section (existing
                  // behaviour, lets the user pick from the list above).
                  onClick={mode === 'create' ? requestClose : () => guardClose(handleCancelCreate)}
                  disabled={creating}
                  className="flex-1 border border-casevo-border text-casevo-forest text-sm py-2 rounded hover:border-casevo-mid transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleCreate()}
                  disabled={createDisabled}
                  className="flex-1 bg-casevo-primary text-white text-sm py-2 rounded hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </div>
          )}
        </div>
        )}
        </div>
      </div>
    </div>
  )
}
export default ClientPickerModal
